import { fromJS, Map } from 'immutable'
import get from 'lodash/get'
import includes from 'lodash/includes'

import { Feed } from 'store/records/feed'
import * as types from './actionTypes'
import { fetchLeaderBoard, deleteStoryApiRequest } from './actions'
import { ACTION_NAME_COMMIT, ACTION_NAME_DID, TAB_STORIES } from 'app/constants/feed'
import { updateStoryInFeed, findIndexByStory } from 'helpers/parseStories'

const initialState = new Feed()

const setRecentToState = (state, payload) => {
  const feed = fromJS(payload.data.data)
  const page = get(payload, 'data.pagination.page')
  let recent
  // remove duplication if from socket we get currently performed activity
  if (page === 0) {
    recent = feed
  } else {
    recent = state.get('recent').concat(feed)
  }
  return state
    .setIn(['pending', 'getRecent'], false)
    .setIn(['loaded', 'getRecent'], true)
    .set('recent', recent)
    .set('recentPagination', new Map(payload.data.pagination))
}

const setRecentLoadMoreToState = (state, payload) => {
  const feed = fromJS(payload.data.data)
  const recent = state.get('recent').concat(feed)
  return state
    .setIn(['pending', 'getRecentLoadMore'], false)
    .setIn(['loaded', 'getRecentLoadMore'], !feed.size)
    .set('recent', recent)
    .set('recentPagination', new Map(payload.data.pagination))
}

const setOneStoryEvent = (state, newEvent) => {
  const isSelfStory = newEvent.selfStory
  const stories = state.get('stories')
  const recent = state.get('recent')
  const storiesPagination = state.get('storiesPagination')
  const recentPagination = state.get('recentPagination')

  newEvent.timestamp = Date.now()
  newEvent.isNewEvent = true

  state = setPagination(state, stories, storiesPagination)
  state = setPagination(state, recent, recentPagination)
  state = state.update('stories', stories => stories.unshift(fromJS(newEvent)))

  if (!isSelfStory) {
    state = state.set('unreadStoriesCount', state.get('unreadStoriesCount') + 1)
    state = state.get('tab') !== TAB_STORIES ? state.set('lastUnreadStoryAuthor', fromJS(newEvent.user)) : state
  }
  return state.update('recent', recent => recent.unshift(fromJS(newEvent)))
}

const setOneRecentEvent = (state, newEvent) => {
  newEvent.timestamp = Date.now()
  newEvent.isNewEvent = true

  const recent = state.get('recent')
  const recentPagination = state.get('recentPagination')
  const recentActionOfTheSameUserIndex = recent.findIndex(el => {
    return (
      newEvent.event &&
      el.getIn(['user', 'email'], null) === get(newEvent, 'user.email', null) &&
      el.getIn('event', 'activityName') === newEvent.event.activityName &&
      includes([ACTION_NAME_COMMIT, ACTION_NAME_DID], el.getIn('event', 'name')) &&
      includes([ACTION_NAME_COMMIT, ACTION_NAME_DID], newEvent.event.name) &&
      // less than 5 min (60 * 5 * 1000)
      newEvent.timestamp - el.get('timestamp') < 300000
    )
  })
  if (recentActionOfTheSameUserIndex !== -1) {
    return state.update('recent', recent => recent.set(recentActionOfTheSameUserIndex, fromJS(newEvent)))
  }

  state = setPagination(state, recent, recentPagination)
  return state.update('recent', recent => recent.unshift(fromJS(newEvent)))
}

const setPagination = (state, list, pagination) => {
  const { limit, page } = pagination.toJS()
  const currentListPage = list.size / limit
  const roundedPage = Math.floor(currentListPage)

  if (roundedPage === page && currentListPage > page) {
    const updatedLimit = limit - (list.size - limit * page)
    state = state.setIn(['recentPagination', 'limit'], updatedLimit)
  } else if (roundedPage > page) {
    state = state.setIn(['recentPagination', 'page'], roundedPage)
  }
  return state
}

const setStoriesToState = (state, payload) => {
  const stories = fromJS(payload.data.data)
  return state
    .setIn(['pending', 'getStories'], false)
    .setIn(['loaded', 'getStories'], true)
    .set('stories', stories)
    .set('storiesPagination', new Map(payload.data.pagination))
}

const setStoriesLoadMoreToState = (state, payload) => {
  const recentStories = fromJS(payload.data.data)
  const stories = state.get('stories').concat(recentStories)
  return state
    .setIn(['pending', 'getStoriesLoadMore'], false)
    .setIn(['loaded', 'getStoriesLoadMore'], !recentStories.size)
    .set('stories', stories)
    .set('storiesPagination', new Map(payload.data.pagination))
}

const updateFeedReactionByIndex = (state, newReaction, recentIndex, recentReactionIndex) => {
  if (typeof recentReactionIndex === 'number' && recentReactionIndex !== -1) {
    return state.setIn(['recent', recentIndex, 'reactions', recentReactionIndex], fromJS(newReaction))
  }
  return state.updateIn(['recent', recentIndex, 'reactions'], reactions => reactions.unshift(fromJS(newReaction)))
}

const updateStoryReactionByIndex = (state, newReaction, storyIndex, storyReactionIndex) => {
  if (typeof storyReactionIndex === 'number' && storyReactionIndex !== -1) {
    return state.setIn(['stories', storyIndex, 'reactions', storyReactionIndex], fromJS(newReaction))
  }
  return state.updateIn(['stories', storyIndex, 'reactions'], reactions => reactions.unshift(fromJS(newReaction)))
}

const setFeedReaction = (state, newReaction) => {
  const { activityUserStoryId, eventId } = newReaction
  const isStoryReaction = Boolean(activityUserStoryId)
  const recent = state.get('recent')
  const stories = state.get('stories')
  const recentIndex = recent.findIndex(
    item =>
      isStoryReaction
        ? item.getIn(['activityUserStory', 'id']) === activityUserStoryId
        : item.getIn(['event', 'id']) === eventId
  )
  if (recentIndex !== -1) {
    state = updateFeedReactionByIndex(state, newReaction, recentIndex)
  }

  if (isStoryReaction && stories.size) {
    const storyIndex = stories.findIndex(item => item.getIn(['activityUserStory', 'id']) === activityUserStoryId)
    if (storyIndex === -1) {
      return state
    }
    state = updateStoryReactionByIndex(state, newReaction, storyIndex)
  }
  return state
}

const setFeedReactionFromSocket = (state, newReaction) => {
  const { activityUserStoryId, eventId, user } = newReaction
  const isStoryReaction = Boolean(activityUserStoryId)
  const recent = state.get('recent')
  const stories = state.get('stories')
  const recentIndex = recent.findIndex(
    item =>
      isStoryReaction
        ? item.getIn(['activityUserStory', 'id']) === activityUserStoryId
        : item.getIn(['event', 'id']) === eventId
  )
  if (recentIndex !== -1) {
    const recentReactionIndex = state
      .getIn(['recent', recentIndex, 'reactions'])
      .findIndex(
        item =>
          item.getIn(['user', 'id']) === user.id &&
          (isStoryReaction ? item.get('activityUserStoryId') === activityUserStoryId : item.get('eventId') === eventId)
      )
    state = updateFeedReactionByIndex(state, newReaction, recentIndex, recentReactionIndex)
  }

  if (isStoryReaction && stories.size) {
    const storyIndex = stories.findIndex(item => item.getIn(['activityUserStory', 'id']) === activityUserStoryId)
    if (storyIndex === -1) {
      return state
    }

    const storyReactionIndex = state
      .getIn(['stories', storyIndex, 'reactions'])
      .findIndex(
        item => item.getIn(['user', 'id']) === user.id && item.get('activityUserStoryId') === activityUserStoryId
      )
    state = updateStoryReactionByIndex(state, newReaction, storyIndex, storyReactionIndex)
  }
  return state
}

const updateFeedReaction = (state, newReaction) => {
  const { activityUserStoryId, eventId, id } = newReaction
  const isStoryReaction = Boolean(activityUserStoryId)
  const recent = state.get('recent')
  const stories = state.get('stories')
  const recentIndex = recent.findIndex(
    item =>
      isStoryReaction
        ? item.getIn(['activityUserStory', 'id']) === activityUserStoryId
        : item.getIn(['event', 'id']) === eventId
  )
  if (recentIndex !== -1) {
    const recentReactionIndex = state
      .getIn(['recent', recentIndex, 'reactions'])
      .findIndex(item => item.get('id') === id)
    state = updateFeedReactionByIndex(state, newReaction, recentIndex, recentReactionIndex)
  }

  if (isStoryReaction && stories.size) {
    const storyIndex = stories.findIndex(item => item.getIn(['activityUserStory', 'id']) === activityUserStoryId)
    if (storyIndex === -1) {
      return state
    }

    const storyReactionIndex = state
      .getIn(['stories', storyIndex, 'reactions'])
      .findIndex(item => item.get('id') === id)
    state = updateStoryReactionByIndex(state, newReaction, storyIndex, storyReactionIndex)
  }
  return state
}

const updateFeedStory = (state, payload) => {
  const { activity, activityUserStoryId } = payload.data
  const findRecentIndex = findIndexByStory(state, activityUserStoryId, 'recent')
  if (findRecentIndex !== -1) {
    state = updateStoryInFeed(state, findRecentIndex, 'activity', activity)
  }

  const stories = state.get('stories')
  if (stories.size === 0) {
    return state
  }

  const findStoryIndex = findIndexByStory(state, activityUserStoryId, 'stories')
  if (findStoryIndex !== -1) {
    state = updateStoryInFeed(state, findStoryIndex, 'activity', activity, 'stories')
  }

  return state
}

const updateStory = (state, payload) => {
  const { activityUserStory: { id, story } } = payload
  const findRecentIndex = findIndexByStory(state, id, 'recent')
  if (findRecentIndex !== -1) {
    state = updateStoryInFeed(state, findRecentIndex, 'story', story)
  }

  const stories = state.get('stories')
  if (stories.size === 0) {
    return state
  }

  const findStoryIndex = findIndexByStory(state, id, 'stories')
  if (findStoryIndex !== -1) {
    state = updateStoryInFeed(state, findStoryIndex, 'story', story, 'stories')
  }

  return state
}

const deleteStory = (state, { storyId }) => {
  const recent = state.get('recent')
  const findRecentIndex = findIndexByStory(state, storyId, 'recent')
  if (findRecentIndex !== -1) {
    state = state.set('recent', recent.delete(findRecentIndex))
  }

  const stories = state.get('stories')
  if (stories.size === 0) {
    return state
  }

  const findStoryIndex = findIndexByStory(state, storyId, 'stories')
  if (findStoryIndex !== -1) {
    state = state.set('stories', stories.delete(findStoryIndex))
  }
  return state
}

function markStoriesRead(state) {
  let stories = state.get('stories')
  stories = stories.map(item => item.set('read', true))
  state = state.set('stories', stories)
  return state.set('unreadStoriesCount', 0)
}

const feedReducer = (state = initialState, action) => {
  const { type, payload } = action
  switch (type) {
    case types.FEED_LEADER_BOARD_SET_SECTION: {
      return state.setIn(['leaderBoard', 'section'], payload.section)
    }
    case types.FEED_SET_TAB: {
      return state.set('tab', payload.tab)
    }
    case fetchLeaderBoard.toString(): {
      return state.setIn(['pending', 'getLeaderBoard'], true)
    }
    case fetchLeaderBoard.success.toString(): {
      const list = fromJS(payload.data)
      return state.setIn(['pending', 'getLeaderBoard'], false).setIn(['leaderBoard', 'list'], list)
    }
    case fetchLeaderBoard.fail.toString(): {
      return state.setIn(['pending', 'getLeaderBoard'], false)
    }
    case types.FEED_RECENT_ENABLE_GET_PENDING: {
      return state.setIn(['pending', 'getRecent'], true)
    }
    case types.FEED_RECENT_ENABLE_LOAD_MORE_PENDING: {
      return state.setIn(['pending', 'getRecentLoadMore'], true)
    }
    case types.FEED_RECENT.GET.SUCCESS: {
      return setRecentToState(state, payload)
    }
    case types.FEED_RECENT_FROM_SOCKETS: {
      const { feedRecentSingle } = payload
      return setOneRecentEvent(state, feedRecentSingle)
    }
    case types.FEED_STORY_FROM_SOCKETS: {
      const { storySingle } = payload
      return setOneStoryEvent(state, storySingle)
    }
    case types.FEED_RECENT.GET.FAIL: {
      return state.setIn(['pending', 'getRecent'], false)
    }
    case types.FEED_RECENT_LOAD_MORE.GET.SUCCESS: {
      return setRecentLoadMoreToState(state, payload)
    }
    case types.FEED_RECENT_SCROLL: {
      const { scrollTop } = payload
      return state.set('scrollTop', scrollTop.recent)
    }
    case types.FEED_SET_FILTER_BY_TEAM: {
      const { team } = payload
      return state.setIn(['filter', 'byTeam'], team)
    }
    case types.FEED_SET_FILTER_BY_RANGE: {
      const { range } = payload
      return state.setIn(['filter', 'byRange'], range)
    }
    case types.FEED_STORIES.GET.SUCCESS: {
      return setStoriesToState(state, payload)
    }
    case types.FEED_STORIES.GET.FAIL: {
      return state.setIn(['pending', 'getStories'], false)
    }
    case types.FEED_STORIES_LOAD_MORE.GET.SUCCESS: {
      return setStoriesLoadMoreToState(state, payload)
    }
    case types.FEED_STORIES_SCROLL: {
      const { scrollTop } = payload
      return state.set('scrollTop', scrollTop.stories)
    }
    case types.FEED_STORIES_ENABLE_GET_PENDING: {
      return state.setIn(['pending', 'getStories'], true)
    }
    case types.FEED_STORIES_ENABLE_LOAD_MORE_PENDING: {
      return state.setIn(['pending', 'getStoriesLoadMore'], true)
    }
    case types.FEED_SET_EVENT_LIKE_OLD: {
      return state.setIn(['recent', payload.index, 'isNewEvent'], false)
    }
    case types.FEED_SET_STORY_LIKE_OLD: {
      return state.setIn(['stories', payload.index, 'isNewEvent'], false)
    }
    case types.FEED_SET_REACTION_FROM_SOCKETS: {
      const { reaction } = payload
      return setFeedReactionFromSocket(state, reaction)
    }
    case types.FEED_UPDATE_REACTION_FROM_SOCKETS: {
      const { reaction } = payload
      return updateFeedReaction(state, reaction)
    }
    case types.FEED_SET_REACTION: {
      return setFeedReaction(state, payload)
    }
    case types.FEED_UPDATE_REACTION: {
      const { reaction, emoji } = payload
      return updateFeedReaction(state, { ...reaction, emoji })
    }
    case types.FEED_STORY_ACTIVITY.GET.SUCCESS: {
      console.log('here = ', payload)

      return updateFeedStory(state, payload)
    }
    case types.FEED_REMOVE_UNREAD_STORY_AUTHOR: {
      return state.set('lastUnreadStoryAuthor', null)
    }
    case types.FEED_STORIES_UNREAD_COUNT.GET.SUCCESS: {
      return state.set('unreadStoriesCount', payload.data.unreadCount)
    }
    case types.FEED_STORIES_MARKED_READ.GET.SUCCESS: {
      return markStoriesRead(state)
    }
    case types.FEED_UPDATE_STORY_FROM_SOCKETS: {
      return updateStory(state, payload)
    }
    case deleteStoryApiRequest.success.toString(): {
      return deleteStory(state, payload)
    }
    default:
      return state
  }
}

export default feedReducer
